import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { getAuthToken } from "../../utils/getAuthToken";

// Async thunk to create a new bulk message campaign
export const createBulkMessageCampaign = createAsyncThunk(
  'bulkMessageCampaign/createCampaign',
  async (campaignData, { rejectWithValue }) => {
    try {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
      if (!token) {
        console.error("❌ No token found for campaign creation");
        return rejectWithValue("Authentication token not found. Please log in again.");
      }
      
      // Get API URL with fallback
      const apiUrl = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:3000';
      
      console.log("=== CREATING BULK MESSAGE CAMPAIGN ===");
      console.log("Campaign data being sent:", campaignData);
      console.log("API URL:", `${apiUrl}/api/bulk-messages/campaigns/create`);
      console.log("Environment REACT_APP_SERVER_DOMAIN:", process.env.REACT_APP_SERVER_DOMAIN);
      console.log("Token details:", {
        hasToken: !!token,
        tokenLength: token?.length || 0,
        tokenPreview: token ? token.substring(0, 20) + "..." : "No token"
      });
      
      const response = await axios.post(
        `${apiUrl}/api/bulk-messages/campaigns/create`,
        campaignData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      console.log("Campaign creation response:", response.data);
      return response.data;
    } catch (error) {
      console.error("Campaign creation error:", error);
      console.error("Error response:", error.response?.data);
      return rejectWithValue(
        error.response?.data?.message || 'Failed to create bulk message campaign'
      );
    }
  }
);

// Async thunk to update an existing (still-draft) bulk message campaign
export const updateBulkMessageCampaign = createAsyncThunk(
  'bulkMessageCampaign/updateCampaign',
  async ({ campaignId, ...updateData }, { rejectWithValue }) => {
    try {
      const token = await getAuthToken();
      if (!token) {
        return rejectWithValue("Authentication token not found. Please log in again.");
      }

      const apiUrl = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:3000';

      const response = await axios.put(
        `${apiUrl}/api/bulk-messages/campaigns/${campaignId}`,
        updateData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update bulk message campaign'
      );
    }
  }
);

// Async thunk to start a campaign
export const startBulkMessageCampaign = createAsyncThunk(
  'bulkMessageCampaign/startCampaign',
  async ({ campaignId }, { rejectWithValue }) => {
    try {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
      if (!token) {
        console.error("❌ No token found for campaign start");
        return rejectWithValue("Authentication token not found. Please log in again.");
      }
      
      const apiUrl = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:3000';
      
      const response = await axios.post(
        `${apiUrl}/api/bulk-messages/campaigns/${campaignId}/start`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to start bulk message campaign'
      );
    }
  }
);

// Async thunk to get campaign status
export const getBulkMessageCampaignStatus = createAsyncThunk(
  'bulkMessageCampaign/getStatus',
  async ({ campaignId }, { rejectWithValue }) => {
    try {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
      if (!token) {
        console.error("❌ No token found for campaign status");
        return rejectWithValue("Authentication token not found. Please log in again.");
      }
      
      const apiUrl = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:3000';
      
      const response = await axios.get(
        `${apiUrl}/api/bulk-messages/campaigns/${campaignId}/status`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to get campaign status'
      );
    }
  }
);

// Async thunk to cancel a campaign
export const cancelBulkMessageCampaign = createAsyncThunk(
  'bulkMessageCampaign/cancelCampaign',
  async ({ campaignId }, { rejectWithValue }) => {
    try {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
      if (!token) {
        console.error("❌ No token found for campaign cancellation");
        return rejectWithValue("Authentication token not found. Please log in again.");
      }
      
      const apiUrl = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:3000';
      
      const response = await axios.post(
        `${apiUrl}/api/bulk-messages/campaigns/${campaignId}/cancel`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to cancel bulk message campaign'
      );
    }
  }
);

const initialState = {
  currentCampaign: null,
  campaignStatus: null,
  loading: false,
  error: null,
  success: null,
  progress: {
    current: 0,
    total: 0,
    status: ''
  }
};

const bulkMessageCampaignSlice = createSlice({
  name: 'bulkMessageCampaign',
  initialState,
  reducers: {
    clearCampaign: (state) => {
      state.currentCampaign = null;
      state.campaignStatus = null;
      state.error = null;
      state.success = null;
      state.progress = initialState.progress;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
    updateProgress: (state, action) => {
      state.progress = { ...state.progress, ...action.payload };
    },
    setCurrentCampaign: (state, action) => {
      state.currentCampaign = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Create campaign
      .addCase(createBulkMessageCampaign.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(createBulkMessageCampaign.fulfilled, (state, action) => {
        state.loading = false;
        state.currentCampaign = action.payload.data;
        state.success = action.payload.message;
        state.error = null;
      })
      .addCase(createBulkMessageCampaign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = null;
      })
      // Update campaign
      .addCase(updateBulkMessageCampaign.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateBulkMessageCampaign.fulfilled, (state, action) => {
        state.loading = false;
        state.currentCampaign = action.payload.data;
        state.success = action.payload.message;
        state.error = null;
      })
      .addCase(updateBulkMessageCampaign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = null;
      })
      // Start campaign
      .addCase(startBulkMessageCampaign.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(startBulkMessageCampaign.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload.message;
        state.error = null;
      })
      .addCase(startBulkMessageCampaign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Get campaign status
      .addCase(getBulkMessageCampaignStatus.fulfilled, (state, action) => {
        state.campaignStatus = action.payload.data;
        if (action.payload.data) {
          state.progress = {
            current: action.payload.data.progress.sent,
            total: action.payload.data.progress.total,
            status: action.payload.data.status
          };
        }
      })
      // Cancel campaign — backend moves the campaign back to draft (PENDING)
      // rather than a terminal CANCELLED state.
      .addCase(cancelBulkMessageCampaign.fulfilled, (state, action) => {
        state.success = action.payload.message;
        if (state.campaignStatus) {
          state.campaignStatus.status = action.payload.data?.status || 'PENDING';
        }
      });
  }
});

export const {
  clearCampaign,
  clearError,
  clearSuccess,
  updateProgress,
  setCurrentCampaign
} = bulkMessageCampaignSlice.actions;

export default bulkMessageCampaignSlice.reducer;
